import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import Kpi from '../components/Kpi';
import { formatDate, formatDateTime } from '../lib/format';
import PageHeader from '../components/PageHeader';
import type { CommunityPartner, PartnerUpdate } from '../lib/types';

export default function CommunityDashboardPage() {
  const [partners, setPartners] = useState<CommunityPartner[]>([]);
  const [updates, setUpdates] = useState<PartnerUpdate[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    void load();
  }, []);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const [p, u] = await Promise.all([
        supabase.from('community_partners').select('*').order('sort_order', { ascending: true }),
        supabase
          .from('community_partner_updates')
          .select('*')
          .order('update_date', { ascending: false })
          .limit(20),
      ]);
      if (p.error) throw p.error;
      if (u.error) throw u.error;
      setPartners((p.data ?? []) as CommunityPartner[]);
      setUpdates((u.data ?? []) as PartnerUpdate[]);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'حصل خطأ');
    } finally {
      setLoading(false);
    }
  };

  const activePartners = partners.filter((p) => p.active).length;
  const totalOffers = partners.reduce((s, p) => s + p.offers.length, 0);
  const totalBranches = partners.reduce((s, p) => s + p.branches.length, 0);

  const partnerName = (id: string) => partners.find((p) => p.id === id)?.name ?? id;

  return (
    <>
      <PageHeader
        eyebrow="مجتمع مرسال"
        title="التقارير"
        subtitle="أرقام سريعة عن الشركاء والعروض والتحديثات"
      />

      {error && <div className="error-inline">{error}</div>}

      {loading ? (
        <div className="empty">جاري التحميل...</div>
      ) : (
        <>
          <div className="kpi-grid">
            <Kpi label="إجمالي الشركاء" value={partners.length} />
            <Kpi label="شركاء نشطين" value={activePartners} />
            <Kpi label="إجمالي العروض المعروضة" value={totalOffers} />
            <Kpi label="إجمالي الفروع المتاحة" value={totalBranches} />
            <Kpi label="آخر التحديثات المضافة" value={updates.length} hint="أحدث 20" />
          </div>

          <div className="card">
            <h3>الشركاء والعروض</h3>
            <table className="table">
              <thead>
                <tr>
                  <th>الشريك</th>
                  <th>الحالة</th>
                  <th>عدد العروض</th>
                  <th>عدد الفروع</th>
                  <th>عدد الخدمات</th>
                </tr>
              </thead>
              <tbody>
                {partners.length === 0 && (
                  <tr>
                    <td colSpan={5} className="empty">
                      مفيش شركاء لسه.
                    </td>
                  </tr>
                )}
                {partners.map((p) => (
                  <tr key={p.id}>
                    <td>{p.name}</td>
                    <td>
                      {p.active ? (
                        <span className="badge badge-success">نشط</span>
                      ) : (
                        <span className="badge badge-muted">موقوف</span>
                      )}
                    </td>
                    <td>{p.offers.length}</td>
                    <td>{p.branches.length}</td>
                    <td>{p.services.length}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="card">
            <h3>آخر تحديثات الشركاء</h3>
            {updates.length === 0 ? (
              <div className="empty">مفيش تحديثات مضافة.</div>
            ) : (
              <table className="table">
                <thead>
                  <tr>
                    <th>التاريخ</th>
                    <th>الشريك</th>
                    <th>التحديث</th>
                    <th>تم الإضافة</th>
                  </tr>
                </thead>
                <tbody>
                  {updates.map((u) => (
                    <tr key={u.id}>
                      <td>{formatDate(u.update_date)}</td>
                      <td>{partnerName(u.partner_id)}</td>
                      <td>{u.body}</td>
                      <td>{formatDateTime(u.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}
    </>
  );
}
